import type { TagSortOrder } from '@mnd-frontend/graphql-schema';
import IconSearch from '@mnd-frontend/icons/components/Search';
import { Button, Checkbox, Input, InputRow, Select } from '@mnd-frontend/ui/publish/components';
import { NEUTRAL_WHITE } from '@mnd-frontend/ui/shared/tokens';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTagIdsQuery } from '../../../generated/hooks';

const Header = styled.div`
  background: ${NEUTRAL_WHITE};
`;

interface SortOptions {
  label: string;
  value: TagSortOrder;
}

const DesktopHeader = ({
  totalCount,
  selectedIds,
  searchQuery,
  pressroomId,
  sortOptions,
  selectedSort,
  onCombineClick,
  onDeleteClick,
  onSelectedIdsChanged,
  onSearchQueryChanged,
  onSelectedSortChanged,
}: {
  totalCount: number;
  searchQuery: string;
  pressroomId: string;
  selectedIds: string[];
  sortOptions: SortOptions[];
  selectedSort: TagSortOrder;
  onCombineClick: () => void;
  onDeleteClick: () => void;
  onSelectedIdsChanged: (ids: string[]) => void;
  onSearchQueryChanged: (query: string) => void;
  onSelectedSortChanged: (sort: TagSortOrder) => void;
}) => {
  const [t] = useTranslation();
  const { refetch: getTagIds } = useTagIdsQuery({ skip: true });

  return (
    <Header>
      <InputRow $nowrap $grow>
        <Input
          trailingIcon={<IconSearch />}
          label={t('category_management_search_label')}
          onChange={e => onSearchQueryChanged(e.target.value)}
          value={searchQuery}
        />
        <Select
          label={t('common_sorting')}
          options={sortOptions}
          onChange={sort => sort && onSelectedSortChanged(sort)}
          value={selectedSort}
        />
      </InputRow>
      <InputRow $justifyContent="start">
        <Checkbox
          onChange={async e => {
            if (e.target.checked) {
              try {
                const result = await getTagIds({
                  pressroomId,
                  query: searchQuery,
                  perPage: totalCount,
                  sortOrder: selectedSort,
                  page: 1,
                });
                onSelectedIdsChanged(result.data.pressroom?.tags2.hits.map(tag => tag.id) ?? []);
              } catch (err) {
                // eslint-disable-next-line no-console
                console.error(err);
              }
            } else {
              onSelectedIdsChanged([]);
            }
          }}
          state={
            selectedIds.length === 0
              ? 'unchecked'
              : selectedIds.length === totalCount
                ? 'checked'
                : 'indeterminate'
          }
          label={t('bulk-actions_selected-count', {
            totalCount,
            count: selectedIds.length,
          })}
        />
        <Button
          type="button"
          configuration="text"
          onClick={onCombineClick}
          disabled={selectedIds.length < 2}
        >
          {t('category_management_combine')}
        </Button>
        <Button
          type="button"
          configuration="text"
          disabled={selectedIds.length === 0}
          onClick={onDeleteClick}
        >
          {t('common_delete')}
        </Button>
      </InputRow>
    </Header>
  );
};

export default React.memo(DesktopHeader);
